import { useEffect, useRef } from "react";
import {
  useApiFileDownload,
  useApiGet,
  useApiMutate,
  useQueryClient,
} from "@skanska/vsa-client-auth";
import { ApiEndpoints } from "../models/ApiEndpoints";
import { ApiMethods } from "../models/ApiMethods";
import {
  PublishedDocumentMetadataMigrationJob,
  PublishedDocumentMetadataMigrationStartRequest,
  PublishedDocumentMetadataMigrationWorkerStatus,
} from "../models/PublishedDocumentMetadataMigration";

const queryKey = "published-document-metadata-migration";

function useMigrationMutationErrorHandling() {
  const queryClient = useQueryClient();
  queryClient.setMutationDefaults([queryKey], { throwOnError: false });
}

export function usePublishedDocumentMetadataMigrationWorkerStatus() {
  return useApiGet<PublishedDocumentMetadataMigrationWorkerStatus>(
    ApiEndpoints.PublishedDocumentMetadataMigrations,
    "status",
    [queryKey, "worker-status"]
  );
}

export function usePublishedDocumentMetadataMigration(jobId?: string) {
  const queryClient = useQueryClient();
  const startedPollingAt = useRef(0);
  const errorCount = useRef(0);
  const result = useApiGet<PublishedDocumentMetadataMigrationJob>(
    ApiEndpoints.PublishedDocumentMetadataMigrations,
    jobId ?? null,
    [queryKey, jobId],
    { enabled: Boolean(jobId) }
  );

  useEffect(() => {
    if (!jobId || document.hidden || !navigator.onLine) return;
    if (startedPollingAt.current === 0) startedPollingAt.current = Date.now();
    const terminal = result.data && [2, 3, 4, 5].includes(result.data.status);
    if (terminal) return;
    errorCount.current = result.error ? errorCount.current + 1 : 0;
    const elapsed = Date.now() - startedPollingAt.current;
    const delay = result.error
      ? [10_000, 20_000, 30_000][Math.min(errorCount.current - 1, 2)]
      : elapsed < 500
        ? 500
        : elapsed < 10_000
          ? 2_000
          : 5_000;
    const timer = window.setTimeout(
      () => queryClient.invalidateQueries({ queryKey: [queryKey, jobId] }),
      delay
    );
    return () => window.clearTimeout(timer);
  }, [jobId, queryClient, result.data, result.error]);

  useEffect(() => {
    const refresh = () => {
      if (!document.hidden && navigator.onLine && jobId) {
        queryClient.invalidateQueries({ queryKey: [queryKey, jobId] });
      }
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [jobId, queryClient]);

  return result;
}

export function useStartPublishedDocumentMetadataMigration() {
  useMigrationMutationErrorHandling();
  return useApiMutate<
    PublishedDocumentMetadataMigrationJob,
    PublishedDocumentMetadataMigrationStartRequest
  >(
    ApiEndpoints.PublishedDocumentMetadataMigrations,
    ApiMethods.Post,
    null,
    null,
    [queryKey]
  );
}

export function useCancelPublishedDocumentMetadataMigration(jobId?: string) {
  useMigrationMutationErrorHandling();
  return useApiMutate<PublishedDocumentMetadataMigrationJob, void>(
    `${ApiEndpoints.PublishedDocumentMetadataMigrations}/${jobId}/cancel`,
    ApiMethods.Post,
    null,
    null,
    [queryKey]
  );
}

export function usePublishedDocumentMetadataMigrationExceptions(
  jobId?: string
) {
  return useApiFileDownload(
    `${ApiEndpoints.PublishedDocumentMetadataMigrations}/${jobId}/exceptions`,
    null
  );
}
